import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';

/**
 * Licensed LLMs — pick from Master LLM catalog (same UX as connectors/flows).
 * Empty list = legacy (no filter). Custom free-text rows still supported.
 */
const LicenseLlmAssets = ({
  agenticEnabled = true,
  llmCatalog = [],
  llmDraft = [],
  onChange,
  readOnly = false,
}) => {
  const [addOpen, setAddOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [draftIds, setDraftIds] = useState(() => new Set());
  const [customProvider, setCustomProvider] = useState('');
  const [customModel, setCustomModel] = useState('');

  const selectedMasterIds = useMemo(() => {
    const s = new Set();
    (llmDraft || []).forEach((l) => {
      if (l.masterLlmId) s.add(String(l.masterLlmId));
    });
    return s;
  }, [llmDraft]);

  const rows = useMemo(() => {
    const byId = new Map((llmCatalog || []).map((c) => [String(c.masterLlmId), c]));
    return (llmDraft || []).map((l, idx) => {
      const cat = l.masterLlmId ? byId.get(String(l.masterLlmId)) : null;
      const name =
        l.displayName ||
        cat?.displayName ||
        [l.provider, l.modelCode].filter(Boolean).join(' / ') ||
        'LLM';
      const type = l.provider || cat?.provider || '';
      return {
        key: l.masterLlmId ? `m:${l.masterLlmId}` : `c:${idx}:${l.provider}:${l.modelCode}`,
        idx,
        name,
        type,
        modelCode: l.modelCode || cat?.modelCode || '',
      };
    });
  }, [llmCatalog, llmDraft]);

  const catalogForDialog = useMemo(() => {
    return (llmCatalog || [])
      .filter((c) => c.masterLlmId && !selectedMasterIds.has(String(c.masterLlmId)))
      .map((c) => ({
        id: String(c.masterLlmId),
        name: c.displayName || `${c.provider} / ${c.modelCode}`,
        type: c.provider || '',
        modelCode: c.modelCode || '',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [llmCatalog, selectedMasterIds]);

  const filteredCatalog = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalogForDialog;
    return catalogForDialog.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        String(r.type || '')
          .toLowerCase()
          .includes(q) ||
        String(r.modelCode || '')
          .toLowerCase()
          .includes(q)
    );
  }, [catalogForDialog, search]);

  const openAdd = () => {
    setAddOpen(true);
    setSearch('');
    setDraftIds(new Set());
  };

  const closeAdd = () => {
    setAddOpen(false);
    setDraftIds(new Set());
    setSearch('');
  };

  const confirmAdd = () => {
    if (draftIds.size === 0) return;
    const byId = new Map((llmCatalog || []).map((c) => [String(c.masterLlmId), c]));
    const next = [...(llmDraft || [])];
    draftIds.forEach((id) => {
      const c = byId.get(id);
      if (!c) return;
      next.push({
        masterLlmId: id,
        provider: c.provider || '',
        modelCode: c.modelCode || '',
        displayName: c.displayName || '',
        enabled: true,
        monthlyTokenLimit: 2000000,
      });
    });
    onChange?.(next);
    closeAdd();
  };

  const confirmCustom = () => {
    const model = (customModel || '').trim();
    if (!model) return;
    onChange?.([
      ...(llmDraft || []),
      {
        provider: (customProvider || '').trim() || 'OpenAI',
        modelCode: model,
        enabled: true,
        monthlyTokenLimit: 2000000,
      },
    ]);
    setCustomOpen(false);
    setCustomProvider('');
    setCustomModel('');
  };

  const removeAt = (idx) => {
    onChange?.((llmDraft || []).filter((_, i) => i !== idx));
  };

  const toggleDraft = (id) => {
    setDraftIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Box sx={{ opacity: agenticEnabled ? 1 : 0.55 }}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          px={1.5}
          py={1}
          sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}
        >
          <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              Licensed LLMs
            </Typography>
            <Chip
              size="small"
              label={`${rows.length} selected`}
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          </Stack>
          <Stack direction="row" spacing={0.75}>
            <Button
              size="small"
              disabled={!agenticEnabled || readOnly}
              onClick={() => {
                setCustomProvider('');
                setCustomModel('');
                setCustomOpen(true);
              }}
            >
              Custom
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!agenticEnabled || readOnly}
              onClick={openAdd}
            >
              Add
            </Button>
          </Stack>
        </Box>

        {!agenticEnabled && (
          <Alert severity="info" sx={{ borderRadius: 0, py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
            Enable Agentic AI to edit licensed models.
          </Alert>
        )}

        {rows.length === 0 ? (
          <Box px={2} py={2.5} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              None yet. Click Add to choose from Master LLM. Empty = no filter (legacy).
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 260, overflow: 'auto' }}>
            {rows.map((r, idx) => (
              <ListItem
                key={r.key}
                dense
                divider={idx > 0}
                secondaryAction={
                  <Tooltip title="Remove from license">
                    <span>
                      <IconButton
                        edge="end"
                        size="small"
                        color="error"
                        disabled={!agenticEnabled || readOnly}
                        onClick={() => removeAt(r.idx)}
                        aria-label={`Remove ${r.name}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                }
                sx={{ py: 0.75, pr: 6 }}
              >
                <ListItemText
                  primary={r.name}
                  secondary={
                    r.modelCode ? `${r.type}${r.type ? ' · ' : ''}${r.modelCode}` : r.type || null
                  }
                  primaryTypographyProps={{ variant: 'body2', noWrap: true, title: r.name }}
                  secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      <Dialog open={addOpen} onClose={closeAdd} maxWidth="sm" fullWidth>
        <DialogTitle>Add from Master LLM</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            size="small"
            placeholder="Search provider or model…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 1.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {filteredCatalog.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
              {catalogForDialog.length === 0
                ? 'All catalog models are already on this license, or the catalog is empty.'
                : 'No matches.'}
            </Typography>
          ) : (
            <List dense disablePadding sx={{ maxHeight: 360, overflow: 'auto' }}>
              {filteredCatalog.map((r) => {
                const checked = draftIds.has(r.id);
                return (
                  <ListItemButton key={r.id} dense onClick={() => toggleDraft(r.id)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                    </ListItemIcon>
                    <ListItemText
                      primary={r.name}
                      secondary={r.modelCode ? `${r.type} · ${r.modelCode}` : r.type}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdd}>Cancel</Button>
          <Button variant="contained" disabled={draftIds.size === 0} onClick={confirmAdd}>
            Add {draftIds.size > 0 ? `(${draftIds.size})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={customOpen} onClose={() => setCustomOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add custom LLM</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} pt={0.5}>
            <TextField
              fullWidth
              size="small"
              label="Provider"
              value={customProvider}
              onChange={(e) => setCustomProvider(e.target.value)}
              placeholder="OpenAI"
            />
            <TextField
              fullWidth
              size="small"
              label="Model code"
              value={customModel}
              onChange={(e) => setCustomModel(e.target.value)}
              placeholder="gpt-4o"
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!String(customModel || '').trim()}
            onClick={confirmCustom}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LicenseLlmAssets;
